<template>
    <ValidationObserver tag="form" ref="observer" @submit.prevent="submit()">
        <ContentOrLoading>
            <b-row v-if="registro">
                <b-col cols="12">
                    <h3>{{ $t('preventa.titulo_formulario_registro') }}</h3>
                    <hr/>
                </b-col>
                <b-col cols="12">
                    <FormInput
                        v-model="registro.n_entradas"
                        id="n_entradas"
                        data-cy="n_entradas"
                        type="number"
                        rules="required"
                        :infoText="$t('preventa.info_max_entradas')"
                        :max="event.n_max_asientos"
                        :disabled="loading"
                        :label="$t('formularios.n_entradas')"
                        :name="$t('formularios.n_entradas')"
                    />
                </b-col>
                <b-col cols="12" md="4">
                    <FormInput
                        v-model="registro.name"
                        id="nombre"
                        data-cy="nombre"
                        rules="required"
                        :disabled="loading"
                        :label="$t('formularios.nombre')"
                        :name="$t('formularios.nombre')"
                    />
                </b-col>
                <b-col cols="12" md="4">
                    <FormInput
                        v-model="registro.surnames"
                        id="apellidos"
                        data-cy="apellidos"
                        rules="required"
                        :disabled="loading"
                        :label="$t('formularios.apellidos')"
                        :name="$t('formularios.apellidos')"
                    />
                </b-col>
                <b-col cols="12" md="4">
                    <DocumentoTipo
                        :disabled="loading"
                        :value="registro.tipoDocumentoSeleccionado?.type"
                        :index="0"
                        @input="val => { registro.tipoDocumentoSeleccionado.type = val; }"
                    />
                </b-col>
                <b-col cols="12" md="4">
                    <DocumentoValor
                        :disabled="loading"
                        :type="registro.tipoDocumentoSeleccionado?.type"
                        :value="registro.nif"
                        :index="0"
                        @input="val => { registro.nif = val; }"
                    />
                </b-col>
                <b-col cols="12" md="4">
                    <div class="form-group">
                        <label>{{ $t('formularios.telefono') }}</label>
                        <PhoneInput
                            :phone="registro.telefono"
                            :desactivar="loading"
                            :required="true"
                            :forceValidate="true"
                            @setNumber="(number) => registro.telefono = number"
                            @validNumber="(valid) => isValidNumber = valid"
                        />
                    </div>
                </b-col>
                <b-col cols="12" md="4">
                    <div class="form-group">
                        <Paises
                            :desactivar="loading"
                            @rellenarPais="rellenarPais"
                            @selected="rellenarPais"
                        />
                    </div>
                </b-col>
                <b-col cols="12" md="4" v-if="registro.pais_id == 68">
                    <div class="form-group">
                        <Provincias
                            :desactivar="loading"
                            :pais="registro.pais?.id"
                            :provincia="registro.provincia"
                            @selected="opt => {
                                if (opt.id !== registro.province_id) {
                                    registro.localidad = null
                                    registro.location_id = null
                                }

                                registro.provincia = opt
                                registro.province_id = opt.id
                            }"
                        />
                    </div>
                </b-col>
                <b-col cols="12" md="4" v-if="registro.pais_id == 68">
                    <div class="form-group">
                        <Locations
                            :key="registro.location_id"
                            :desactivar="loading"
                            :provincia="registro.provincia"
                            :localidad="registro.localidad"
                            @selected="opt => { registro.localidad = opt; registro.location_id = opt.id }"
                        />
                    </div>
                </b-col>
                <b-col cols="12" class="form-group mt-3">
                    <div class="checkbox-container">
                        <ValidationProvider name="condiciones" rules="required" v-slot="{ errors }">
                            <input
                                id="condiciones"
                                name="condiciones"
                                type="checkbox"
                                :true-value="1"
                                :false-value="null"
                                :disabled="loading"
                                v-model="condiciones"
                            />
                            <label for="condiciones" :class="errors[0] ? 'pb-0 pb-md-2' : ''">
                                {{ $t('formularios.he_leido') }}
                                <router-link :to="{ name: 'legal-preventa', params: { eventoId: event.id } }" target="_blank">
                                    <span>{{ $t('formularios.terminos_condiciones') }}</span>
                                </router-link>
                                {{ $t('formularios.de_la_preventa') }}
                            </label>
                            <span v-if="errors[0]" class="invalid-feedback text-xs mt-5 mt-md-4 d-block w-100 bg-danger text-white p-2"><b>{{ errors[0] }}</b></span>
                        </ValidationProvider>
                    </div>
                </b-col>
                <b-col cols="12" v-if="validationErrors" class="mt-3 mb-3">
                    <span v-for="(messages, field) in validationErrors" :key="field" class="invalid-feedback text-xs" style="font-size: 11px; width: 99.72%; display: block; background-color: #DC3545; color: white; padding: 6px;">
                        <b v-for="(msg, i) in messages" :key="i">
                            {{ msg }}
                        </b>
                    </span>
                </b-col>
                <b-col cols="12" class="text-right">
                    <hr/>
                    <button
                        id="registrarse"
                        data-cy="registrarse"
                        type="submit"
                        :class="['button', 'large-button', { 'effect-button': false, 'w-100': esMovil }]"
                        :disabled="loading"
                    >
                        <span>{{ $t('preventa.enviar_registro') }}</span>
                    </button>
                </b-col>
            </b-row>
        </ContentOrLoading>
    </ValidationObserver>
</template>

<script setup>
    import { ref } from 'vue'
    import Paises from "@/components/common/forms/Paises"
    import Locations from "@/components/common/forms/Locations"
    import Provincias from "@/components/common/forms/Provincias"
    import PhoneInput from '@/components/common/PhoneInput.vue'
    import DocumentoTipo from "@/components/common/DocumentoTipo"
    import DocumentoValor from "@/components/common/DocumentoValor"
    import { useRegistroPreventa } from '@/composables/front/events/useRegistroPreventa'

    const props = defineProps({
        event: {
            type: Object,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        validationErrors: {
            type: Object,
            default: null
        }
    })


    const emit = defineEmits(['terminado'])

    const observer = ref(null)
    const isValidNumber = ref(false)
    const condiciones = ref(null)

    const {
        registro,
        esMovil
    } = useRegistroPreventa()

    const rellenarPais = opt => {
        registro.value.pais = opt
        registro.value.pais_id = opt.id
    }

    const submit = async() => {
        const isValid = await observer.value.validate()

        if (isValid && isValidNumber.value) {
            if (registro.value.pais_id != 68) {
                registro.value.provincia = null
                registro.value.province_id = null
                registro.value.localidad = null
                registro.value.location_id = null
            }
            emit('terminado', { ...registro.value })
        }
    }
</script>
